import React, { useEffect } from 'react';

import Icon from 'components/DesignSystem/Icon';
import usePinchZoom from 'utils/hooks/usePinchZoom';
import { cssVar } from 'constants/theme';

import './index.scss';

export interface FullscreenChartProps {
  open: boolean;
  dismiss: () => void;
  src: string;
  alt: string;
  label?: string;
  toPrevious?: () => void;
  toNext?: () => void;
}

/**
 * Tela cheia da carta sinótica: overlay escuro em retrato, sem rotação forçada.
 * Como em retrato a imagem não passa da largura da tela, o pinch-to-zoom + pan é o
 * que de fato permite ler os detalhes da carta.
 */
export default function FullscreenChart({
  open,
  dismiss,
  src,
  alt,
  label,
  toPrevious,
  toNext,
}: FullscreenChartProps) {
  const {
    targetRef,
    isZoomed,
    transform,
    reset,
    toggleZoom,
    onPointerDown,
    onPointerMove,
    onPointerUp,
  } = usePinchZoom();

  // Zoom não deve persistir ao trocar de imagem nem entre aberturas.
  useEffect(() => {
    reset();
  }, [src, open, reset]);

  if (!open) return null;

  const showNav = Boolean(toPrevious || toNext);

  return (
    <div className="sc-mobile-fullscreen-overlay">
      <button
        type="button"
        aria-label="Sair do modo tela cheia"
        className="sc-mobile-fullscreen-close"
        onClick={dismiss}
      >
        <Icon iconType="close" color={cssVar('text-strong')} />
      </button>

      <div
        className="sc-mobile-fullscreen-stage"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={toggleZoom}
      >
        <img
          ref={targetRef}
          className="sc-mobile-fullscreen-image"
          style={{ transform }}
          src={src}
          alt={alt}
          draggable={false}
        />
      </div>

      <div className="sc-mobile-fullscreen-footer">
        {label && <span className="sc-mobile-fullscreen-label">{label}</span>}

        {showNav && (
          <div className="sc-mobile-fullscreen-nav">
            <button
              type="button"
              aria-label="Imagem anterior"
              className="sc-mobile-fullscreen-nav-button"
              disabled={!toPrevious}
              onClick={toPrevious}
            >
              <Icon iconType="left_arrow" color={cssVar('text-strong')} size="small" />
            </button>
            <button
              type="button"
              aria-label="Próxima imagem"
              className="sc-mobile-fullscreen-nav-button"
              disabled={!toNext}
              onClick={toNext}
            >
              <Icon iconType="right_arrow" color={cssVar('text-strong')} size="small" />
            </button>
          </div>
        )}
      </div>

      {!isZoomed && (
        <span className="sc-mobile-fullscreen-hint">Pince para ampliar · toque duplo para zoom</span>
      )}
    </div>
  );
}
